import { ArrowRight } from "lucide-react"
import { Link } from "@inertiajs/react"
import { Button } from "@/components/ui/button"
import { MarketingLayout } from "@/components/marketing/MarketingLayout"
import { MarketingMetadata } from "@/components/marketing/MarketingMetadata"
import { SERVICES_COPY } from "@/content/marketing-services"
import { localizedHomeUrl, marketingBreadcrumbs } from "@/lib/marketing-structured-data"
import type { MarketingPageProps } from "@/types/marketing"

export default function MarketingServices(props: MarketingPageProps) {
  const copy = SERVICES_COPY[props.locale]
  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Service",
        name: props.title,
        description: props.description,
        url: props.canonical_url,
        provider: {
          "@type": "Organization",
          name: "Zmile d.o.o.",
          url: localizedHomeUrl(props.locale),
        },
        hasOfferCatalog: {
          "@type": "OfferCatalog",
          name: copy.services.heading,
          itemListElement: copy.services.items.map((service) => ({
            "@type": "Offer",
            itemOffered: {
              "@type": "Service",
              name: service.title,
              description: service.body,
            },
          })),
        },
      },
      marketingBreadcrumbs({
        canonicalUrl: props.canonical_url,
        locale: props.locale,
        title: props.title,
      }),
    ],
  }

  return (
    <>
      <MarketingMetadata
        alternateUrls={props.alternate_urls}
        canonicalUrl={props.canonical_url}
        description={props.description}
        locale={props.locale}
        noindex={props.noindex}
        socialImageUrl={props.social_image_url}
        structuredData={structuredData}
        title={props.title}
      />
      <MarketingLayout
        currentPageKey={props.page_key}
        footer={props.footer}
        homeHref={props.home_href}
        languageSwitch={props.language_switch}
        locale={props.locale}
        navigation={props.navigation}
        consultationHref={props.consultation_href}
      >
        <section className="services-hero">
          <div className="services-hero-copy">
            <h1 className="type-marketing-hero-display">{copy.hero.heading}</h1>
            <p className="type-marketing-lead">{copy.hero.summary}</p>
            <div className="services-hero-actions">
              <Button asChild variant="marketing" size="marketingLg">
                <Link href={props.consultation_href} data-analytics-id="services-hero-consultation">
                  {copy.final.review}
                  <ArrowRight className="marketing-inline-icon" aria-hidden="true" />
                </Link>
              </Button>
              <Link className="home-secondary-action type-marketing-action" href={props.work_href}>
                {copy.final.conversation}
              </Link>
            </div>
          </div>

          <figure className="services-system" aria-label={copy.hero.systemLabel}>
            <figcaption className="type-marketing-caption type-marketing-muted">
              {copy.hero.systemLabel}
            </figcaption>
            <ol>
              {copy.hero.systemNodes.map((node, index) => (
                <li key={node}>
                  <span className="type-marketing-index type-marketing-signal-display">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <strong className="type-marketing-node">{node}</strong>
                </li>
              ))}
            </ol>
          </figure>
        </section>

        <section className="services-catalog" aria-labelledby="services-catalog-heading">
          <header className="services-section-header">
            <h2 className="type-marketing-section-display" id="services-catalog-heading">
              {copy.services.heading}
            </h2>
            <p className="type-marketing-prose">{copy.services.introduction}</p>
          </header>

          <div className="services-list">
            {copy.services.items.map((service, index) => (
              <article data-service key={service.title}>
                <header>
                  <span className="type-marketing-index type-marketing-signal-display">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <h3 className="type-marketing-node-title">{service.title}</h3>
                </header>
                <p className="type-marketing-prose">{service.body}</p>
                <div className="services-flow" aria-label={service.outcome} role="group">
                  {service.flow.map((step, stepIndex) => (
                    <span className="type-marketing-microcopy" key={step}>
                      {step}
                      {stepIndex < service.flow.length - 1 && <ArrowRight className="marketing-inline-icon" aria-hidden="true" />}
                    </span>
                  ))}
                </div>
                <p className="services-outcome type-marketing-body-small">{service.outcome}</p>
                <p className="services-artifact type-marketing-microcopy">{service.artifact}</p>
                <p className="services-technology-guidance type-marketing-body-small">{service.technologyGuidance}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="services-technology">
          <div className="services-technology-copy">
            <h2 className="type-marketing-section-display type-marketing-inverse">
              {copy.technology.heading}
            </h2>
            <p className="type-marketing-prose type-marketing-inverse-muted">
              {copy.technology.body}
            </p>
          </div>
          <aside className="services-principle">
            <span className="type-marketing-index type-marketing-signal-display" aria-hidden="true">
              IF / THEN
            </span>
            <strong className="type-marketing-panel-display type-marketing-inverse">
              {copy.technology.principle}
            </strong>
            <ul>
              {copy.technology.criteria.map((criterion) => (
                <li className="type-marketing-node type-marketing-inverse" key={criterion}>
                  <span aria-hidden="true">✓</span>
                  {criterion}
                </li>
              ))}
            </ul>
          </aside>
        </section>

        <section className="services-engagement" aria-labelledby="services-engagement-heading">
          <header className="services-section-header">
            <h2 className="type-marketing-section-display" id="services-engagement-heading">
              {copy.engagement.heading}
            </h2>
            <div>
              <p className="type-marketing-prose">{copy.engagement.introduction}</p>
              <Link className="home-secondary-action type-marketing-action" href={props.about_href}>
                {copy.engagement.founderLink}
                <ArrowRight className="marketing-inline-icon" aria-hidden="true" />
              </Link>
            </div>
          </header>
          <ol className="services-engagement-steps">
            {copy.engagement.items.map((item, index) => (
              <li key={item.title}>
                <span className="type-marketing-index type-marketing-signal-display">
                  {String(index + 1).padStart(2, "0")}
                </span>
                <h3 className="type-marketing-node">{item.title}</h3>
                <p className="type-marketing-body-small">{item.body}</p>
              </li>
            ))}
          </ol>
        </section>

        <section className="services-final">
          <div>
            <h2 className="type-marketing-section-display">{copy.final.heading}</h2>
          </div>
          <div>
            <p className="type-marketing-prose">{copy.final.body}</p>
            <div className="services-final-actions">
              <Button asChild variant="marketing" size="marketingLg">
                <Link href={props.consultation_href} data-analytics-id="services-final-consultation">
                  {copy.final.review}
                  <ArrowRight className="marketing-inline-icon" aria-hidden="true" />
                </Link>
              </Button>
              <Link className="home-secondary-action type-marketing-action" href={props.work_href}>
                {copy.final.conversation}
              </Link>
            </div>
          </div>
        </section>
      </MarketingLayout>
    </>
  )
}
